import styled from 'styled-components'
import React, { useEffect, useRef } from 'react'
import { Dict } from 'styled-components/dist/types'
import * as d3 from 'd3'

type LineChartProps = {
  data: DataEntry[]
  colorMode: 'light' | 'dark' | undefined
  title?: string
  subtitle?: string
  labelNames: Dict
}
type DataEntry = {
  year: number | null
  Schweiz: number | null
  Frankreich: number | null
  Niederlande: number | null
  Italien: number | null
  USA: number | null
}

const LineChartCountry = ({
  data,
  colorMode,
  title,
  subtitle,
  labelNames,
}: LineChartProps) => {
  const ref = useRef<SVGSVGElement>()
  const wrapperRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!ref.current || !data || !wrapperRef.current) return
    let tooltip = d3.select(wrapperRef.current).selectAll('.tooltip') as any
    if (tooltip.empty()) {
      // Define font-size based on window width
      let fontSize
      if (window.innerWidth <= 350) {
        fontSize = '12.5px'
      } else if (window.innerWidth < 712) {
        fontSize = '13.5px'
      } else {
        fontSize = '14px'
      }

      // Tooltip Element erstellen
      tooltip = d3
        .select(wrapperRef.current)
        .append('div')
        .attr('class', 'tooltip')
        .style('position', 'absolute')
        .style('z-index', '400')
        .style('background-color', 'white')
        .style(
          'box-shadow',
          `${
            colorMode === 'dark' ? '0 4px 40px #000000' : '0 4px 40px #00000033'
          }`
        )
        .style('border-radius', '5px')
        .style('opacity', 0.01)
        .style('pointer-events', 'none') // Add this line
        .style('font-size', fontSize)
        .style('font-family', 'IBM Plex Sans')
        .style('line-height', '1.2')
    }
    // console.log(all)
    let rightMargin = window.innerWidth < 600 ? 82 : 90
    const margin = { top: 30, right: rightMargin, bottom: 30, left: 7 }
    const width = ref.current.clientWidth - margin.left - margin.right
    const height = ref.current.clientHeight - margin.top - margin.bottom

    const svg = d3.select(ref.current)
    svg.selectChildren().remove()

    const chart = svg
      .append('g')
      .attr('transform', `translate(5, ${margin.top})`) // adjust translation here

    // X axis
    // Define startDomain based on window width
    let startDomain = window.innerWidth < 600 ? 1896.5 : 1900
    let endDomain = window.innerWidth < 600 ? 2019 : 2017
    let x = d3.scaleLinear().domain([startDomain, endDomain]).range([0, width])
    let xTicks = [1920, 1950, 1980, 2010]
    svg.on('mousemove touchmove', (event) => {
      var eventType = event.type === 'touchmove' ? event.touches[0] : event

      const x0 = x.invert(d3.pointer(eventType, wrapperRef.current)[0])

      // Check if year is within required range
      if (x0 < 1910 || x0 > 2020) {
        return
      }

      // get nearest year
      const bisect = d3.bisector((d: DataEntry) => d.year).left
      const i = bisect(data, x0, 1)
      const d0 = data[i - 1]
      const d1 = data[i]
      let d = d0
      if (d1 && d1.year) {
        d = x0 - d0.year! > d1.year! - x0 ? d1 : d0
      }
      // from here on we have d as the nearest data point
      const xPos = x(d.year ?? 0)

      hideTooltip()

      // Add a vertical line
      chart
        .append('line')
        .attr('class', 'tooltip-line')
        .style('stroke-width', '1px')
        .style('stroke', 'var(--chart-grid)')
        .style('pointer-events', 'none')
        .attr('x1', xPos)
        .attr('y1', 0)
        .attr('x2', xPos)
        .attr('y2', height)

      // Make all dots of this year visible
      svg.selectAll(`.dot-year-${d.year}`).style('opacity', 1).raise() // Moves these dots to the front
      const chartWrapperPosition =
        wrapperRef?.current?.getBoundingClientRect() ?? { left: 0, top: 0 }

      let htmlContent = `
      <div style='padding-bottom: 0.25em; background: ${
        colorMode === 'dark' ? '#555555' : '#e9e9e9'
      }; color: ${
        colorMode === 'dark' ? '#e9e9e9' : 'var(--text-color)'
      }; padding: 8px 12px; border-radius: 3px 3px 0 0;'>
      <b>${d.year}</b>
      </div>
      `
      htmlContent += `<div style='padding: 8px 12px; line-height: 1.4; background: ${
        colorMode === 'dark' ? '#242424' : '#ffffff'
      }'>`
      let sortedDataKeys = [...dataKeys].sort(
        (a, b) => (d[b] ?? 0) - (d[a] ?? 0)
      )
      const colorMap: Partial<Record<string, (typeof colors)[number]>> = {}
      dataKeys.forEach((key, index) => {
        if (colors[index]) {
          colorMap[key] = colors[index]
        }
      })
      // Adding all datapoints in the tooltip
      sortedDataKeys.forEach((key) => {
        if (d[key] !== 0 && d[key] !== null) {
          htmlContent += `<span style='color: ${
            colorMap[key]
          }; display: inline-block;'><b>${labelNames[key]}</b>: ${(d[key] ?? 0)
            .toFixed(1)
            .replace('.', ',')}&nbsp;cm</span><br/>`
        }
      })
      htmlContent += `</div>`

      // Define leftPositionOffset based on window width
      let leftPositionOffset = window.innerWidth < 600 ? 185 : 200
      const leftPosition =
        ((d.year ?? 0) > 1985
          ? eventType.pageX - leftPositionOffset
          : eventType.pageX + 35) -
        window.scrollX -
        chartWrapperPosition.left
      const topPositionDesktop =
        eventType.pageY - window.scrollY - chartWrapperPosition.top + 40
      // top position for mobile should be on top of the chart
      const topPositionMobile = 170
      tooltip
        .style('opacity', 1)
        .html(htmlContent)
        .style('left', `${leftPosition}px`)
        .style(
          'top',
          (window.innerWidth < 768
            ? topPositionMobile
            : topPositionDesktop - 45) + 'px'
        )
    })
    svg.on('mouseleave', (event) => {
      hideTooltip()
    })

    chart
      .append('g')
      .attr('class', 'x axis-grid')
      .attr('transform', `translate(0, ${height})`)
      .call(
        d3
          .axisBottom(x)
          .tickFormat(d3.format(''))
          .tickValues(xTicks)
          .tickSizeOuter(0)
      )
      // .call(xAxis)
      .selectAll('text')
      .style('text-anchor', 'middle')

    // Y axis
    let y = d3.scaleLinear().domain([160, 185]).range([height, 0]) // set range here
    const yAxisGrid = d3.axisLeft(y).tickSize(-width).ticks(4)

    chart
      .append('g')
      .attr('class', 'y axis-grid')
      .call(yAxisGrid)
      .call((g) => g.select('.domain').remove())
      .call((g) => {
        // Remove the tick line for first tick
        g.select('.tick:first-of-type line').remove()
        // Remove the tick text for first tick
        g.select('.tick:first-of-type text').remove()
        // add unit cm to the last tick
        g.select('.tick:last-of-type text').text(function (d) {
          return d + ' cm'
        })
      })
      .call((g) =>
        g
          .selectAll('.tick text')
          .attr('x', 0)
          .attr('dy', -4)
          .style('text-anchor', 'start')
      )

    // Line Generators
    const dataKeys: (keyof DataEntry)[] = [
      'Schweiz',
      'Frankreich',
      'Niederlande',
      'Italien',
      'USA',
    ]

    // Colors for each line
    const colors = [
      `${colorMode === 'dark' ? '#e9e9e9' : '#D3343F'}`,
      '#378EBD',
      '#E16D00',
      '#479260',
      '#A757A3',
    ]
    const labelEndOfLine = [
      labelNames['Schweiz'],
      labelNames['Frankreich'],
      labelNames['Niederlande'],
      labelNames['Italien'],
      labelNames['USA'],
    ]
    const lineTextPosition = ['-.1em', '-0.1em', '0.25em', '0.5em', '1.1em']
    //   .attr('dy', '1.65em')

    dataKeys.forEach((key, index) => {
      const lineData = data.filter((item) => item[key] !== null)

      const lineGenerator = d3
        .line<DataEntry>()
        .x((d) => x(d.year ?? 0))
        .y((d) => y(d[key] ?? 0))

      chart
        .append('path')
        .attr('fill', 'none')
        .attr('stroke-width', 2.5)
        .attr('stroke', colors[index])
        .attr('d', lineGenerator(lineData))
      // Check if the data key is 'men_ch'
      // if (key === 'men_ch') {
      //   path.attr('stroke-dasharray', '5,5')
      // }

      // Tooltip handlers for each line
      chart
        .selectAll(`.dot-${key}`)
        .data(lineData)
        .enter()
        .append('circle')
        .attr('class', function (d) {
          return `dot dot-${key} dot-year-${d.year}`
        })
        .style('fill', colors[index])
        .attr('cx', function (d) {
          return x(d.year ?? 0)
        })
        .attr('cy', function (d) {
          return y(d[key] ?? 0)
        })
        .attr('r', 5)
        .style('opacity', 0)

      // Define fontSize based on window width
      let fontSize = window.innerWidth < 712 ? '15px' : '16px'

      chart
        .append('text')
        .attr('text-anchor', 'start')
        .style('font-size', fontSize)
        .attr(
          'transform',
          `translate(${width + 1}, ${y(
            lineData[lineData.length - 1][key] ?? 0
          )})`
        )
        .attr('dy', lineTextPosition[index])
        .style('fill', colors[index])
        .text(labelEndOfLine[index])
    })
    const hideTooltip = () => {
      tooltip.style('opacity', 0)
      chart.selectAll('.tooltip-line').remove()
      svg.selectAll('.dot').style('opacity', 0.01)
    }

    // New code: Create an Intersection Observer to watch if the SVG is in view or not.
    var svgEl = d3.select(ref.current).node() // get SVG node
    var observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          // Check if not intersecting
          hideTooltip()
        }
      })
    })

    if (svgEl !== null) {
      observer.observe(svgEl)
    }

    // only hide on scroll on desktop
    if (window.innerWidth > 768) {
      // console.log('adding scroll eventlistener')
      window.addEventListener('scroll', hideTooltip)
      return () => {
        window.removeEventListener('scroll', hideTooltip)
        // Disconnect Observer on unmount
        observer.disconnect()
      }
    }
  }, [data, colorMode])

  return (
    <Wrapper ref={wrapperRef}>
      <Title>
        {title}
        <div className='subtitle'>{subtitle}</div>
      </Title>
      <LineChartContainer>
        <svg ref={ref as React.RefObject<SVGSVGElement>} />
      </LineChartContainer>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  position: relative;
`
const Title = styled.div`
  flex: 0 0 100%;
  font-size: 23px;
  line-height: 1.4em;
  font-weight: 200;
  letter-spacing: 0.6px;
  text-align: left;
  margin-bottom: 30px;

  .subtitle {
    display: inline-block;
    font-size: 13px;
    line-height: 1.4em;
    width: 100%;
    color: var(--subtitle);
    padding-top: 0.75em;
  }
`

const LineChartContainer = styled.div`
  height: 360px;
  width: 100%;
  text-align: center;
  font-family: var(--font-sans-serif);
  margin-bottom: 25px;
  svg {
    width: 100%;
    height: 100%;
  }
  .axis-grid line {
    stroke: var(--chart-grid);
    stroke-dasharray: 4, 2;
  }
  .tick text {
    font-size: 14px;
    fill: var(--text-color);
  }
  .x.axis-grid {
    .tick line {
      stroke: var(--text-color);
    }
  }
`

export default LineChartCountry
